import { message } from 'antd';
import { useClient, useLooseOrder, usePriceChanges, useUser, useUsers } from 'reducers';
import API, { CreatePaymentDto, CreateTaskDto, EditLooseOrderDto, EditTaskDto } from '../../../../API';
import { OrderType, TaskType } from 'types';

export const useBigBagOrderDetails = (id: number) => {
	const [order, fetchOrder] = useLooseOrder(id);
	const [client] = useClient(order?.clientId);
	const users = useUsers({});
	const [claimingAssignee] = useUser(order?.claimingTask?.assignedTo ?? undefined);
	const [deliveryAssignee] = useUser(order?.deliveryTask?.assignedTo ?? undefined);
	const [priceChanges, fetchPriceChanges] = usePriceChanges(id, OrderType.loose);

	const editOrder = async (dto: EditLooseOrderDto) => {
		if (!order) {
			return;
		}

		try {
			const price = dto.price ?? order.grossPrice;
			const wantedAdvance = dto.wantedAdvance ?? order.wantedAdvance;
			if (wantedAdvance && wantedAdvance < 0) {
				message.error('Zaliczka nie może być mniejsza od 0');
				return;
			}

			if (wantedAdvance && wantedAdvance >= price) {
				message.error('Zaliczka nie może być większa lub równa cenie');
				return;
			}

			await API.looseOrder(id).patch(dto);
			await fetchOrder();
			await fetchPriceChanges();

			message.success('Zamówienie zostało edytowane');

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się edytować zamówienia (${e})`);

			return false;
		}
	};

	const deleteOrder = async () => {
		try {
			await API.looseOrder(id).delete();
			message.success('Zamówienie zostało usunięte');

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się usunąć zamówienia (${e})`);

			return false;
		}
	};

	const editDeliveryTask = async (dto: EditTaskDto) => {
		try {
			if (!order?.deliveryTask) {
				message.error('Zamówienie nie ma zadania dostawy');
				return false;
			}

			await API.tasks.patch(order?.deliveryTask?.id, dto);
			await fetchOrder();

			message.success('Zamówienie zostało edytowane');

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się edytować zamówienia (${e})`);

			return false;
		}
	};

	const editClaimingTask = async (dto: EditTaskDto) => {
		try {
			if (!order?.claimingTask) {
				message.error('Zamówienie nie ma zadania odbioru');
				return false;
			}

			await API.tasks.patch(order?.claimingTask?.id, dto);
			await fetchOrder();

			message.success('Zamówienie zostało edytowane');

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się edytować zamówienia (${e})`);

			return false;
		}
	};

	const deleteClaimingTask = async () => {
		try {
			const claimingTask = order?.claimingTask;
			if (!claimingTask) {
				message.error('Zamówienie nie ma terminu odbioru');
				return false;
			}

			await API.tasks.delete(claimingTask.id);
			await fetchOrder();

			message.success('Termin odbioru został usunięty');
			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się usunąć terminu odbioru (${e})`);

			return false;
		}
	};

	const markAsPaid = async () => {
		try {
			if (!order) {
				message.error('Nie można oznaczyć zamówienia jako opłaconego, ponieważ nie istnieje');
				return false;
			}

			const alreadyPaid = order.payments.reduce((acc, p) => acc + p.amount, 0);

			await API.payments.put({
				orderId: order.id,
				orderType: OrderType.loose,
				amount: order.grossPrice - alreadyPaid,
				type: order.wantedPaymentType,
			} as CreatePaymentDto);
			await fetchOrder();
			message.success('Zamówienie zostało oznaczone jako opłacone');

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się oznaczyć zamówienia jako opłacone (${e})`);

			return false;
		}
	};

	const createTask = async (taskData: CreateTaskDto) => {
		try {
			await API.tasks.put(taskData);
			await fetchOrder();

			message.success(
				`Zadanie ${taskData.type === TaskType.delivery ? 'dostarczenia' : 'odebrania'} zostało utworzone`
			);

			return true;
		} catch (e) {
			console.error(e);
			message.error(`Nie udało się utworzyć zadania (${e})`);

			return false;
		}
	};

	return {
		order,
		client,
		claimingAssignee,
		deliveryAssignee,
		users,
		priceChanges,
		editOrder,
		deleteOrder,
		editClaimingTask,
		editDeliveryTask,
		deleteClaimingTask,
		createTask,
		markAsPaid,
	};
};
